import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const STATUS_STORAGE_KEY = 'outletStatuses';
const PAGE_SIZE_OPTIONS = [10, 20, 50];

const COLORS = {
  forestGreen: '#0F3D2E',
  deepGreen: '#003C3C',
  heritageGold: '#C9A14A',
  offWhite: '#FFFDF7',
  sand: '#F7F3EA',
  border: '#E4D7BC',
  success: '#2F8F4E',
  danger: '#B54135',
  muted: '#6A6257',
};

export default function Outlets() {
  const navigate = useNavigate();
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadStoredStatuses = () => {
    try {
      const stored = localStorage.getItem(STATUS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };

  const saveStoredStatuses = (statuses) => {
    try {
      localStorage.setItem(STATUS_STORAGE_KEY, JSON.stringify(statuses));
    } catch {
      // ignore errors
    }
  };

  useEffect(() => {
    const loadOutlets = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.fetchAdminRestaurants();
        const outletsArray = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        const storedStatuses = loadStoredStatuses();
        const mergedOutlets = outletsArray.map((outlet) => ({
          ...outlet,
          status: storedStatuses[outlet.id] || outlet.status || 'Active',
        }));

        setOutlets(mergedOutlets);
      } catch (error) {
        console.error('Failed to load outlets:', error);
        setError('Failed to load outlets');
      } finally {
        setLoading(false);
      }
    };

    loadOutlets();
  }, []);

  const updateOutlets = (updated) => {
    setOutlets(updated);
  };

  const handleSuspend = async (outlet) => {
    try {
      setActionId(`status-${outlet.id}`);
      const newStatus = outlet.status === 'Suspended' ? 'Active' : 'Suspended';

      await api.updateRestaurant(outlet.id, { status: newStatus });

      updateOutlets(
        outlets.map((item) =>
          item.id === outlet.id ? { ...item, status: newStatus } : item
        )
      );

      const storedStatuses = loadStoredStatuses();
      storedStatuses[outlet.id] = newStatus;
      saveStoredStatuses(storedStatuses);
      setConfirmAction(null);
    } catch (error) {
      console.error('Failed to update outlet status:', error);
      setError(error.message || 'Failed to update outlet status');
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (outlet) => {
    try {
      setActionId(`delete-${outlet.id}`);
      await api.deleteRestaurant(outlet.id);
      const updatedOutlets = outlets.filter((item) => item.id !== outlet.id);
      updateOutlets(updatedOutlets);

      const storedStatuses = loadStoredStatuses();
      delete storedStatuses[outlet.id];
      saveStoredStatuses(storedStatuses);
      setConfirmAction(null);
    } catch (error) {
      console.error('Failed to delete outlet:', error);
      setError(error.message || 'Failed to delete outlet');
    } finally {
      setActionId(null);
    }
  };

  const requestSuspend = (outlet) => {
    const nextActive = outlet.status === 'Suspended';
    setConfirmAction({
      title: `${nextActive ? 'Activate' : 'Suspend'} outlet`,
      message: `${nextActive ? 'Activate' : 'Suspend'} ${outlet.name}?`,
      confirmLabel: nextActive ? 'Activate outlet' : 'Suspend outlet',
      tone: nextActive ? 'success' : 'danger',
      onConfirm: () => handleSuspend(outlet),
    });
  };

  const requestDelete = (outlet) => {
    setConfirmAction({
      title: 'Delete outlet',
      message: `Delete ${outlet.name}? This cannot be undone.`,
      confirmLabel: 'Delete outlet',
      tone: 'danger',
      onConfirm: () => handleDelete(outlet),
    });
  };

  const totalPages = Math.max(1, Math.ceil(outlets.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginatedOutlets = outlets.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <Layout>
      <PageHeader
        eyebrow="Restaurants"
        title="Outlets"
        subtitle="Manage restaurant outlets, owner assignment, and platform availability."
        action={
          <button
            style={styles.createButton}
            onClick={() => navigate('/outlets/create')}
            aria-label="Create new outlet">
            Create Outlet
          </button>
        }
      />

      {loading ? (
        <p>Loading outlets...</p>
      ) : error ? (
        <p style={styles.errorText}>{error}</p>
      ) : outlets.length === 0 ? (
        <p>No outlets found.</p>
      ) : (
        <>
          <div style={styles.tableControls}>
            <span style={styles.resultMeta}>
              Showing {(safePage - 1) * pageSize + 1}-
              {Math.min(safePage * pageSize, outlets.length)} of {outlets.length}{' '}
              outlets
            </span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              style={styles.pageSizeSelect}
              aria-label="Outlets per page"
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} per page
                </option>
              ))}
            </select>
          </div>

          <div style={styles.tableShell}>
            <table style={styles.table} role="table" aria-label="List of outlets">
              <thead style={styles.thead}>
                <tr>
                  <th style={styles.th} scope="col">
                    Name
                  </th>
                  <th style={styles.th} scope="col">
                    Address
                  </th>
                  <th style={styles.th} scope="col">
                    Phone
                  </th>
                  <th style={styles.th} scope="col">
                    Status
                  </th>
                  <th style={styles.th} scope="col">
                    Owner Assigned
                  </th>
                  <th style={{ ...styles.th, ...styles.actionsHeader }} scope="col">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
            {paginatedOutlets.map((outlet, index) => {
              // CHECK IF OWNER EXISTS:
              // We accept either user object with id/_id/email
              const hasOwner =
                outlet.user &&
                (outlet.user.id || outlet.user._id || outlet.user.email);

              return (
                <tr
                  key={outlet.id}
                  style={index % 2 === 0 ? styles.evenRow : styles.oddRow}
                >
                  <td style={styles.td}>{outlet.name}</td>
                  <td style={styles.td}>{outlet.address}</td>
                  <td style={styles.td}>{outlet.phone}</td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.status,
                        backgroundColor:
                          outlet.status === 'Active'
                            ? COLORS.success
                            : outlet.status === 'Suspended'
                            ? COLORS.danger
                            : COLORS.muted,
                      }}
                      aria-label={`Status: ${outlet.status}`}
                    >
                      {outlet.status || 'Unknown'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.assignedBtn,
                        backgroundColor: hasOwner ? COLORS.success : COLORS.danger,
                      }}
                      aria-label={
                        hasOwner ? 'Owner assigned' : 'Owner not assigned'
                      }
                    >
                      {hasOwner ? 'Assigned' : 'Not Assigned'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actionGroup}>
                    <button
                      style={{
                        ...styles.actionBtn,
                        backgroundColor: COLORS.forestGreen,
                      }}
                      onClick={() => navigate(`/outlets/${outlet.id}`)}
                      aria-label={`View details of ${outlet.name}`}
                    >
                      View
                    </button>
                    <button
                      style={{
                        ...styles.actionBtn,
                        backgroundColor: COLORS.deepGreen,
                      }}
                      onClick={() => navigate(`/outlets/${outlet.id}/edit`)}
                      aria-label={`Edit ${outlet.name}`}
                    >
                      Edit
                    </button>
                    <button
                      style={{
                        ...styles.actionBtn,
                        backgroundColor: COLORS.heritageGold,
                      }}
                      onClick={() => requestSuspend(outlet)}
                      disabled={actionId === `status-${outlet.id}`}
                      aria-label={
                        outlet.status === 'Suspended'
                          ? `Activate ${outlet.name}`
                          : `Suspend ${outlet.name}`
                      }
                    >
                      {actionId === `status-${outlet.id}`
                        ? 'Saving...'
                        : outlet.status === 'Suspended'
                        ? 'Activate'
                        : 'Suspend'}
                    </button>
                    <button
                      style={{
                        ...styles.actionBtn,
                        backgroundColor: COLORS.danger,
                      }}
                      onClick={() => requestDelete(outlet)}
                      disabled={actionId === `delete-${outlet.id}`}
                      aria-label={`Delete ${outlet.name}`}
                    >
                      {actionId === `delete-${outlet.id}` ? 'Deleting...' : 'Delete'}
                    </button>
                    </div>
                  </td>
                </tr>
              );
            })}
              </tbody>
            </table>
          </div>

          <div style={styles.paginationBar}>
            <button
              type="button"
              style={styles.pageButton}
              disabled={safePage === 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </button>
            <span style={styles.pageText}>
              Page {safePage} of {totalPages}
            </span>
            <button
              type="button"
              style={styles.pageButton}
              disabled={safePage === totalPages}
              onClick={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
            >
              Next
            </button>
          </div>
        </>
      )}

      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel}
        tone={confirmAction?.tone}
        loading={!!actionId}
        onCancel={() => setConfirmAction(null)}
        onConfirm={confirmAction?.onConfirm}
      />
    </Layout>
  );
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 20,
    alignItems: 'center',
  },
  createButton: {
    padding: '10px 20px',
    backgroundColor: '#C9A14A',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 16,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#FFFDF7',
    minWidth: 1120,
  },
  tableShell: {
    width: '100%',
    overflowX: 'auto',
    borderRadius: 8,
    border: '1px solid #E4D7BC',
    boxShadow: '0 14px 32px rgba(15, 61, 46, 0.08)',
  },
  thead: {
    backgroundColor: '#0F3D2E',
    color: '#fff',
    textAlign: 'left',
  },
  th: {
    padding: '15px 18px',
    fontWeight: 'bold',
  },
  td: {
    padding: '16px 18px',
    borderBottom: '1px solid #E4D7BC',
    verticalAlign: 'middle',
    color: '#003C3C',
  },
  evenRow: {
    backgroundColor: '#FFFDF7',
  },
  oddRow: {
    backgroundColor: '#F7F3EA',
  },
  status: {
    color: '#fff',
    padding: '6px 12px',
    borderRadius: 12,
    fontSize: 12,
    display: 'inline-block',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  actionBtn: {
    backgroundColor: '#0F3D2E',
    color: '#fff',
    padding: '7px 12px',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '700',
    userSelect: 'none',
    lineHeight: 1,
    minHeight: 32,
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 2px rgba(15, 61, 46, 0.12)',
  },
  actionGroup: {
    display: 'grid',
    gridTemplateColumns: '72px 72px 98px 82px',
    alignItems: 'center',
    gap: 8,
    minWidth: 348,
  },
  actionsHeader: {
    minWidth: 348,
  },
  tableControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  resultMeta: {
    color: COLORS.muted,
    fontSize: 13,
    fontWeight: 800,
  },
  pageSizeSelect: {
    minWidth: 140,
    padding: '9px 10px',
    borderRadius: 6,
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.deepGreen,
    fontWeight: 800,
  },
  paginationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '12px 0',
  },
  pageButton: {
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    borderRadius: 6,
    padding: '8px 11px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  pageText: {
    color: COLORS.muted,
    fontWeight: 800,
    fontSize: 13,
  },
  assignedBtn: {
    color: '#fff',
    padding: '3px 18px',
    border: 'none',
    fontSize: 14,
    fontWeight: '400',
    userSelect: 'none',
    borderRadius: 12,
    display: 'inline-block',
  },
  errorText: {
    color: COLORS.danger,
    backgroundColor: '#FCEAE8',
    border: `1px solid ${COLORS.danger}`,
    padding: 12,
    borderRadius: 8,
    fontWeight: 800,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#FFFDF7',
    padding: 24,
    borderRadius: 8,
    width: 320,
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    fontSize: 16,
    fontWeight: '500',
  },
  modalBtn: {
    padding: '8px 18px',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 14,
  },
};
